import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { SingleUnit } from "../types/schema";
import {
    PMConfiguration,
    PMNotificationSetting,
    PMStatus,
    PMContextType,
    NextServiceInfo,
} from "../types/pmTypes";
import { calculatePMStatus, getNextServiceInfo, getPrimaryServiceLevel, serviceLevelsOnSchedulingScale } from "../utils/pmUtils";
import { authClient } from "../utils/amplifyClient";
import { useUserContext } from "./UserContext";
import { useEquipmentContext } from "./EquipmentContext";
import { isUnitArchived, pmSchedulingUM } from "../components/pages-equipment/equipmentFieldUtils";
import { iProvider } from "../types";

const defaultPMContext: PMContextType = {
    pmConfigurations: [],
    enrolledUnits: [],
    isLoading: false,
    selectedUnit: null,
    selectedConfig: null,
    setSelectedUnit: () => { },
    refreshPMData: async () => { },
    getStatusForUnit: () => PMStatus.SCHEDULED,
    getNextServiceLevel: () => null,
};

export const PMContext = createContext<PMContextType>(defaultPMContext);

// eslint-disable-next-line react-refresh/only-export-components
export const usePMContext = () => useContext(PMContext);

export const PMProvider = ({ children }: iProvider) => {
    const { companyUser, companyOwner } = useUserContext();
    const { companyUnits } = useEquipmentContext();

    const [pmConfigurations, setPMConfigurations] = useState<PMConfiguration[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState<SingleUnit | null>(null);
    const [selectedConfig, setSelectedConfig] = useState<PMConfiguration | null>(null);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId;

    const enrolledUnits: SingleUnit[] = companyUnits.filter(
        (unit) => !isUnitArchived(unit) && pmConfigurations.some((config) => config.unitId === unit.id),
    );

    const refreshPMData = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            // Fetch PM configurations for this company
            const { data: configItems } = await authClient.models.PMConfiguration.listPMConfigurationByCompanyId({ companyId });

            // For each config, fetch related service levels, parts, and notification settings
            const configs: PMConfiguration[] = await Promise.all(
                configItems.map(async (item) => {
                    const [serviceLevelsResult, partsResult, notificationsResult] = await Promise.all([
                        authClient.models.PMServiceLevel.listPMServiceLevelByPmConfigurationId({ pmConfigurationId: item.id }),
                        authClient.models.PMPartsAndProcedure.listPMPartsAndProcedureByPmConfigurationId({ pmConfigurationId: item.id }),
                        authClient.models.PMNotificationSetting.listPMNotificationSettingByPmConfigurationId({ pmConfigurationId: item.id }),
                    ]);

                    return {
                        id: item.id,
                        companyId: item.companyId,
                        unitId: item.unitId,
                        unitOfMeasure: item.unitOfMeasure,
                        dueSoonPercentage: item.dueSoonPercentage ?? 0.1,
                        serviceLevels: serviceLevelsResult.data ?? [],
                        partsAndProcedures: partsResult.data ?? [],
                        laborCategoryId: item.laborCategoryId ?? null,
                        // Gen2 `recipients` is Nullable<string>[]; the client-side PMNotificationSetting
                        // type narrows it to string[]. Cast at the boundary without changing the value.
                        notificationSettings: (notificationsResult.data ?? []) as PMNotificationSetting[],
                        createdAt: item.createdAt,
                        updatedAt: item.updatedAt,
                    };
                })
            );

            setPMConfigurations(configs);
        } catch (error) {
            console.error("Failed to fetch PM configurations:", error);
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (companyId) {
            refreshPMData();
        } else {
            setPMConfigurations([]);
        }
    }, [companyId, refreshPMData]);

    useEffect(() => {
        if (selectedUnit) {
            const config = pmConfigurations.find((c) => c.unitId === selectedUnit.id) ?? null;
            setSelectedConfig(config);
        } else {
            setSelectedConfig(null);
        }
    }, [selectedUnit, pmConfigurations]);

    const getStatusForUnit = useCallback(
        (unitId: string): PMStatus => {
            const config = pmConfigurations.find((c) => c.unitId === unitId);
            if (!config) return PMStatus.SCHEDULED;

            const unit = companyUnits.find((u) => u.id === unitId);
            const currentUM = pmSchedulingUM(unit);

            const serviceLevels = serviceLevelsOnSchedulingScale(
                config.serviceLevels ?? [],
                unit?.replacedUMCounterValue,
            );
            if (serviceLevels.length === 0) return PMStatus.SCHEDULED;

            const dueSoonPct = config.dueSoonPercentage ?? 0.1;
            const nextService = getNextServiceInfo(currentUM, serviceLevels);
            if (!nextService) return PMStatus.SCHEDULED;

            return calculatePMStatus({
                currentUM,
                targetUM: nextService.targetUM,
                dueSoonPercentage: dueSoonPct,
                intervalUM: getPrimaryServiceLevel(serviceLevels)?.intervalValue,
            });
        },
        [pmConfigurations, companyUnits],
    );

    /**
     * Get the next service info for a config — computes the dynamic next service point
     * and returns all levels due at that point (cascading intervals).
     * Validates: Requirements 1.3, 2.3, 3.1
     */
    const getNextServiceLevel = useCallback(
        (configId: string): NextServiceInfo | null => {
            const config = pmConfigurations.find((c) => c.id === configId);
            if (!config) return null;

            const unit = companyUnits.find((u) => u.id === config.unitId);
            const serviceLevels = serviceLevelsOnSchedulingScale(
                config.serviceLevels ?? [],
                unit?.replacedUMCounterValue,
            );
            if (serviceLevels.length === 0) return null;

            const currentUM = pmSchedulingUM(unit);

            return getNextServiceInfo(currentUM, serviceLevels);
        },
        [pmConfigurations, companyUnits],
    );

    // Due-soon emails go out when a meter reading is saved (notifyPMDueSoon).
    // The hourly pm-notifications run is the backup for a crossing with no form.

    const value: PMContextType = {
        pmConfigurations,
        enrolledUnits,
        isLoading,
        selectedUnit,
        selectedConfig,
        setSelectedUnit: (unit: unknown | null) => setSelectedUnit(unit as SingleUnit | null),
        refreshPMData,
        getStatusForUnit,
        getNextServiceLevel,
    };

    return <PMContext.Provider value={value}>{children}</PMContext.Provider>;
};
